import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function RemindersSection() {
  const [appointments, setAppointments] = useState([]);
  const [activeTab, setActiveTab] = useState("24h");
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [hideAlreadyReminded, setHideAlreadyReminded] = useState(true);

  // 🔹 confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: null, // "reminder", "attended", "no-show"
    appts: [],
    type: null,
    loading: false,
  });

  // 🔹 Fetch appointments
  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "appointments"));
      const data = snapshot.docs.map((docSnap) => {
        const d = docSnap.data();
        const apptDate = d.date?.toDate?.() ?? new Date(d.date ?? new Date(0));
        return {
          id: docSnap.id,
          ...d,
          apptDate,
          reminder24Sent: d.reminder24Sent || false,
          reminder2hSent: d.reminder2hSent || false,
          reminder24SentAt: d.reminder24SentAt || null,
          reminder2hSentAt: d.reminder2hSentAt || null,
          status: d.status || "Confirmed",
          statusUpdatedAt: d.statusUpdatedAt || null,
        };
      });
      data.sort((a, b) => a.apptDate - b.apptDate);
      setAppointments(data);
      setSelectedIds([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  // 🔹 Time windows
  const now = new Date();
  const tomorrowStart = new Date(now);
  tomorrowStart.setDate(now.getDate() + 1);
  tomorrowStart.setHours(0, 0, 0, 0);
  const tomorrowEnd = new Date(now);
  tomorrowEnd.setDate(now.getDate() + 1);
  tomorrowEnd.setHours(23, 59, 59, 999);

  // 🔹 Filter appointments by tab
  let filteredAppointments = appointments.filter((appt) => {
    if (!appt.apptDate) return false;

    switch (activeTab) {
      case "24h":
        return (
          appt.status === "Confirmed" &&
          appt.apptDate >= tomorrowStart &&
          appt.apptDate <= tomorrowEnd &&
          (hideAlreadyReminded
            ? !appt.reminder24Sent && !appt.reminder2hSent
            : true)
        );
      case "2h":
        return (
          appt.status === "Confirmed" &&
          !appt.reminder2hSent &&
          appt.apptDate > now &&
          appt.apptDate <= new Date(now.getTime() + 2 * 60 * 60 * 1000)
        );
      case "sent":
        return (
          (appt.reminder24Sent || appt.reminder2hSent) &&
          appt.status === "Confirmed" // 🔹 only show pending/confirmed in Sent
        );

      case "history":
        return appt.status === "Attended" || appt.status === "No Show";
      default:
        return false;
    }
  });

  // 🔹 Sort history tab latest first
  if (activeTab === "history") {
    filteredAppointments.sort((a, b) => {
      const aTime = a.statusUpdatedAt
        ? new Date(a.statusUpdatedAt).getTime()
        : a.apptDate.getTime();
      const bTime = b.statusUpdatedAt
        ? new Date(b.statusUpdatedAt).getTime()
        : b.apptDate.getTime();
      return bTime - aTime; // newest first
    });
  }

  // 🔹 Send reminder
  const sendReminder = async (appt, type) => {
    try {
      const payload = {
        name: appt.name || "",
        email: appt.email,
        bookingId: appt.bookingId || "",
        date: appt.date,
        time: appt.time,
        service: appt.service,
        branch: appt.branch || "Main Branch",
        consultation_type: appt.consultationType || "General",
        address: appt.address || "N/A",
        city: appt.city || "N/A",
        state: appt.state || "N/A",
        country: appt.country || "N/A",
        message: appt.message || "",
      };

      let endpoint = "";
      if (type === "24h")
        endpoint =
          "https://vpjc-api-805872224468.asia-south1.run.app/api/appointment/reminder24";
      else if (type === "2h")
        endpoint =
          "https://vpjc-api-805872224468.asia-south1.run.app/api/appointment/reminder2h";

      if (!endpoint) return;

      await axios.post(endpoint, payload);

      const apptRef = doc(db, "appointments", appt.id);
      if (type === "24h") {
        await updateDoc(apptRef, {
          reminder24Sent: true,
          reminder24SentAt: new Date().toISOString(),
        });
      } else if (type === "2h") {
        await updateDoc(apptRef, {
          reminder2hSent: true,
          reminder2hSentAt: new Date().toISOString(),
        });
      }

      fetchAppointments();
    } catch (err) {
      console.error("Failed to send reminder:", err);
      throw err;
    }
  };

  // 🔹 Send Attended / No Show
  const sendStatusEmail = async (appt, type) => {
    try {
      const payload = {
        name: appt.name || "",
        email: appt.email,
        bookingId: appt.bookingId || "", // ✅ added bookingId
        date: appt.date,
        time: appt.time,
        service: appt.service,
        branch: appt.branch || "Main Branch",
        consultation_type: appt.consultationType || "General",
        address: appt.address || "N/A",
        city: appt.city || "N/A",
        state: appt.state || "N/A",
        country: appt.country || "N/A",
        message: appt.message || "",
      };

      let endpoint = "";
      if (type === "no-show")
        endpoint =
          "https://vpjc-api-805872224468.asia-south1.run.app/api/appointment/no-show";
      else if (type === "thank-you")
        endpoint =
          "https://vpjc-api-805872224468.asia-south1.run.app/api/appointment/thank-you";

      if (!endpoint) return;

      await axios.post(endpoint, payload);

      // Update status in Firestore
      const apptRef = doc(db, "appointments", appt.id);
      await updateDoc(apptRef, {
        status: type === "no-show" ? "No Show" : "Attended",
        statusUpdatedAt: new Date().toISOString(),
      });

      fetchAppointments();
    } catch (err) {
      console.error("Failed to send status email:", err);
      throw err;
    }
  };

  // 🔹 Bulk reminders or status
  const sendBulkAction = async (type) => {
    const selectedAppts = filteredAppointments.filter((appt) =>
      selectedIds.includes(appt.id)
    );
    for (const appt of selectedAppts) {
      if (type === "24h" || type === "2h") {
        await sendReminder(appt, type);
      } else if (type === "thank-you" || type === "no-show") {
        await sendStatusEmail(appt, type);
      }
    }
  };

  // 🔹 Handle selection
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredAppointments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAppointments.map((a) => a.id));
    }
  };

  // 🔹 Handle confirm action
  // confirm action
  const handleConfirmAction = async () => {
    setConfirmDialog((prev) => ({ ...prev, loading: true })); // 🔹 start loader
    try {
      if (confirmDialog.action === "reminder") {
        for (const appt of confirmDialog.appts) {
          await sendReminder(appt, confirmDialog.type);
        }
        toast.success("Reminder sent successfully ✅");
      } else if (confirmDialog.action === "attended") {
        for (const appt of confirmDialog.appts) {
          await sendStatusEmail(appt, "thank-you");
        }
        toast.success("Marked as Attended ✅");
      } else if (confirmDialog.action === "no-show") {
        for (const appt of confirmDialog.appts) {
          await sendStatusEmail(appt, "no-show");
        }
        toast.success("Marked as No Show ✅");
      }

      // 🔹 After success, reload appointments → moves from Sent to History
      await fetchAppointments();
    } catch (err) {
      toast.error("Action failed ❌");
    } finally {
      setConfirmDialog({
        open: false,
        action: null,
        appts: [],
        type: null,
        loading: false,
      });
    }
  };

  return (
    <div className="rounded-lg shadow-lg bg-white border p-4 overflow-x-auto">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
        <h2 className="text-lg font-bold text-[#204080]">Reminders</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { key: "24h", label: "1-Day Reminder" },
            { key: "2h", label: "2-Hour Reminder" },
            { key: "sent", label: "Mark Visit Status" },
            { key: "history", label: "History" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-3 py-1 rounded ${
                activeTab === key ? "bg-[#204080] text-white" : "bg-gray-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "24h" && (
        <div className="mb-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={hideAlreadyReminded}
              onChange={() => setHideAlreadyReminded(!hideAlreadyReminded)}
            />
            Hide already reminded
          </label>
        </div>
      )}

      {selectedIds.length > 0 &&
        (activeTab === "2h" || activeTab === "sent") && (
          <div className="mb-3 flex flex-wrap gap-2">
            {activeTab === "2h" && (
              <Button
                onClick={() =>
                  setConfirmDialog({
                    open: true,
                    action: "reminder",
                    appts: filteredAppointments.filter((appt) =>
                      selectedIds.includes(appt.id)
                    ),
                    type: "2h",
                  })
                }
              >
                Send {selectedIds.length} Reminder(s)
              </Button>
            )}
            {activeTab === "sent" && (
              <>
                <Button
                  onClick={() =>
                    setConfirmDialog({
                      open: true,
                      action: "attended",
                      appts: filteredAppointments.filter((appt) =>
                        selectedIds.includes(appt.id)
                      ),
                      type: null,
                    })
                  }
                >
                  Mark Attended
                </Button>
                <Button
                  onClick={() =>
                    setConfirmDialog({
                      open: true,
                      action: "no-show",
                      appts: filteredAppointments.filter((appt) =>
                        selectedIds.includes(appt.id)
                      ),
                      type: null,
                    })
                  }
                >
                  Mark No Show
                </Button>
              </>
            )}
          </div>
        )}

      {loading ? (
        <p>Loading...</p>
      ) : filteredAppointments.length === 0 ? (
        <p className="text-gray-500 italic">No appointments in this tab</p>
      ) : (
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-blue-50 text-[#204080]">
            <tr>
              <th className="px-2 py-2">
                {activeTab !== "history" && (
                  <input
                    type="checkbox"
                    onChange={toggleSelectAll}
                    checked={
                      selectedIds.length === filteredAppointments.length &&
                      filteredAppointments.length > 0
                    }
                  />
                )}
              </th>
              <th className="px-4 py-2 text-left">Patient</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Date</th>
              <th className="px-4 py-2 text-left">Time</th>
              <th className="px-4 py-2 text-left">Service</th>
              {activeTab === "history" && (
                <th className="px-4 py-2 text-left">Marked At</th>
              )}
              <th className="px-4 py-2 text-left">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredAppointments.map((appt) => (
              <tr key={appt.id}>
                <td className="px-2 py-2">
                  {activeTab !== "history" && (
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(appt.id)}
                      onChange={() => toggleSelect(appt.id)}
                      disabled={
                        activeTab === "24h" &&
                        (appt.reminder24Sent || appt.reminder2hSent)
                      }
                    />
                  )}
                </td>
                <td className="px-4 py-2">{appt.name}</td>
                <td className="px-4 py-2">{appt.email}</td>
                <td className="px-4 py-2">{appt.date}</td>
                <td className="px-4 py-2">{appt.time}</td>
                <td className="px-4 py-2">{appt.service}</td>
                {activeTab === "history" && (
                  <td className="px-4 py-2">
                    {appt.statusUpdatedAt
                      ? new Date(appt.statusUpdatedAt).toLocaleString()
                      : "-"}
                  </td>
                )}
                <td className="px-4 py-2">
                  <div className="flex flex-wrap gap-2">
                    {activeTab !== "sent" && activeTab !== "history" && (
                      <Button
                        className="text-xs px-2 py-1 bg-[#204080] hover:bg-[#2E5AAC]"
                        onClick={() =>
                          setConfirmDialog({
                            open: true,
                            action: "reminder",
                            appts: [appt],
                            type: activeTab,
                          })
                        }
                        disabled={appt.reminder24Sent || appt.reminder2hSent}
                      >
                        {appt.reminder24Sent || appt.reminder2hSent
                          ? "Already Sent"
                          : "Send Reminder"}
                      </Button>
                    )}

                    {activeTab !== "24h" && activeTab !== "history" && (
                      <>
                        <Button
                          className="text-xs px-2 py-1 bg-green-600"
                          onClick={() =>
                            setConfirmDialog({
                              open: true,
                              action: "attended",
                              appts: [appt],
                              type: null,
                            })
                          }
                        >
                          Mark Attended
                        </Button>
                        <Button
                          className="text-xs px-2 py-1 bg-red-600"
                          onClick={() =>
                            setConfirmDialog({
                              open: true,
                              action: "no-show",
                              appts: [appt],
                              type: null,
                            })
                          }
                        >
                          Mark No Show
                        </Button>
                      </>
                    )}

                    {activeTab === "sent" && (
                      <>
                        {appt.reminder24Sent && (
                          <span className="text-green-600">
                            24h sent{" "}
                            {appt.reminder24SentAt
                              ? `(${new Date(
                                  appt.reminder24SentAt
                                ).toLocaleString()})`
                              : ""}
                          </span>
                        )}
                        {appt.reminder2hSent && (
                          <span className="text-blue-600">
                            2h sent{" "}
                            {appt.reminder2hSentAt
                              ? `(${new Date(
                                  appt.reminder2hSentAt
                                ).toLocaleString()})`
                              : ""}
                          </span>
                        )}
                      </>
                    )}

                    {activeTab === "history" && (
                      <span
                        className={`font-semibold ${
                          appt.status === "Attended"
                            ? "text-green-600"
                            : "text-red-600"
                        }`}
                      >
                        {appt.status}
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Action</DialogTitle>
            <DialogDescription>
              {confirmDialog.appts.length > 1 ? (
                <>
                  Are you sure you want to{" "}
                  {confirmDialog.action === "reminder"
                    ? "send reminders to the selected patients"
                    : confirmDialog.action === "attended"
                    ? "mark selected patients as Attended"
                    : "mark selected patients as No Show"}
                  ?
                </>
              ) : (
                <>
                  Are you sure you want to{" "}
                  {confirmDialog.action === "reminder"
                    ? `Send a reminder to ${
                        confirmDialog.appts[0]?.name || "this patient"
                      }`
                    : confirmDialog.action === "attended"
                    ? `Mark ${
                        confirmDialog.appts[0]?.name || "this patient"
                      } as Attended`
                    : `Mark ${
                        confirmDialog.appts[0]?.name || "this patient"
                      } as No Show`}
                  ?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() =>
                setConfirmDialog({
                  open: false,
                  action: null,
                  appts: [],
                  type: null,
                  loading: false,
                })
              }
              disabled={confirmDialog.loading} // 🔹 disable cancel while loading
            >
              Cancel
            </Button>
            <Button
              className="bg-[#204080] hover:bg-[#2E5AAC]"
              onClick={handleConfirmAction}
              disabled={confirmDialog.loading} // 🔹 disable confirm
            >
              {confirmDialog.loading ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    ></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
